import { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { useEffect, useState } from "react";
import api from "../../../services/ApiService";
import { ApiResponse } from "../../../models/ApiResponse";
import { SettingGrid } from "../../../components/ManageSettingComponents/SettingGrid";
import { Button, Switch } from "@mui/material";
import { toast } from "react-toastify";
import DataFieldTypeModal from "./DataFieldTypeModal";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import { DataFieldWithTypes } from "../../../models/DataFieldDetail";
import DeleteValidationModal from "../../../components/ManageSettingComponents/DeleteValidationModal";
import { SessionDetails } from "../../../models/SessionDetails";
import GridCellChipsContent from "../../../components/GridCellChipsContent";
import { GRID_ACTION_DELETE_ICON, GRID_ACTION_EDIT_ICON, GRID_READ_ONLY } from "../../../components/GridUtilityComponents";
import { Permissions } from "../../../models/Enum";
import SessionService from "../../../services/SessionService";
import { GridRenderCellParams, GridRowModes, GridRowModesModel, GridRowParams, useGridApiRef } from "@mui/x-data-grid";
import { DataTypeDetail } from "../../../models/DataTypeDetail";

export type CellError = {
  [key: string]: {
    [key: string]: string;
  };
};

export default function DataFieldTypeSetting() {
  const apiRef = useGridApiRef();
  const DataFieldTypeGridCollumns: GridColDef[] = [
    { field: "id", headerName: "Id", disableColumnMenu: true, width: 100 },
    { field: "field", headerName: "CVL Name", disableColumnMenu: true, width: 300 },
    {
      field: "types",
      headerName: "CVL Options",
      width: 1000,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return params.value;
        } else {
          return <GridCellChipsContent showViewMore={params.row?.totalCount > 20} viewMoreHandle={() => handleEditClickOpen(params)} keyField="id" displayTextFiled="description" cellData={params.row?.types} />;
        }
      },
    },
    {
      field: "isActive", headerName: "Is Active", sortable: false, disableColumnMenu: true, width: 150,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.readOnlyField)
          return GRID_READ_ONLY();
        const isCubeMultiplier = params?.row?.field === "CubeMultiplier";
        return (
          <Switch
            checked={params.value}
            onChange={async (e) => {
              await updateDataFieldType(params.row, e.target.checked);
            }}
            color="primary"
            inputProps={{ "aria-label": "controlled" }}
            disabled={!manageAppSetting || isCubeMultiplier}
          />
        );
      }
    },
    {
      field: "actions", type: "actions", disableColumnMenu: true, headerName: "Actions",
      getActions: (params: GridRowParams) => {
        if (params.row.readOnlyField) {
          return [
            GRID_READ_ONLY()
          ];
        }
        const isCubeMultiplier = params?.row?.field === "CubeMultiplier";
        return [
          GRID_ACTION_EDIT_ICON(params, handleEditClickOpen),
          ...(isCubeMultiplier ? [] : [GRID_ACTION_DELETE_ICON(params, handleDeleteModalOpen)])
        ];
      }
    }
  ];

  const manageAppSetting = SessionService.getInstance().hasPermission(Permissions.ManageAppSettings);
  const [dataFieldTypeList, setDataFieldTypeList] = useState<DataFieldWithTypes[]>([]);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [typesValues, setTypesValues] = useState<DataTypeDetail[]>([]);
  const [dataField, setDataField] = useState<DataFieldWithTypes>(null);
  const [editingRows, setEditingRows] = useState<DataTypeDetail[]>([]);
  const [submitValues, setSubmitValues] = useState<DataFieldWithTypes>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cellErrors, setCellErrors] = useState<CellError>({});
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  useEffect(() => {
    fetchDataList();
  }, []);

  useEffect(() => {
    if((cellErrors && Object.keys(cellErrors)?.length > 0) || typesValues?.find(item => item.description === "")) {
      setDisableSubmit(true);
    } else {
      setDisableSubmit(false);
    }
  }, [cellErrors, typesValues])

  const fetchDataList = async () => {
    const getDataFieldTypeResponse = await api.get<
      ApiResponse<DataFieldWithTypes[]>
    >("/Picklist/cvls");
    setDataFieldTypeList(getDataFieldTypeResponse?.data || []);
  };

  const handleEditClickOpen = (gridParams: GridRenderCellParams) => {
    formik.resetForm();
    formik.setValues({ ...gridParams.row });
    setDisableSubmit(false);
    setIsLoading(true);
    setShowAddUpdateModal(true);
  };

  const handleAddClickOpen = () => {
    formik.resetForm();
    setShowAddUpdateModal(true);
    setDisableSubmit(false);
  };

  const handleDeleteModalOpen = (gridParams: GridRowParams) => {
    setDeleteModalOpen(true);
    formik.setValues({ ...gridParams.row });
  };

  const handleCloseAddEditModal = async () => {
    setShowAddUpdateModal(false);
    formik.resetForm();
    clearAllStates();
    await fetchDataList();
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    formik.resetForm();
  };

  //updating datafield only for the switch in active 
  const updateDataFieldType = async (row: DataFieldWithTypes, isActive: boolean) => {
    const data = {
      id: row.id,
      isActive: isActive
    };
    try {
      api
        .patch<ApiResponse<boolean>>("/Picklist/cvl", data)
        .then(async (response) => {
          if (response.isSuccess) {
            toast.success(response.message);
            await fetchDataList();
          } else {
            throw new Error(response.message);
          }
        })
        .catch(() => { });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const deleteSubmit = () => {
    try {
      api
        .delete<ApiResponse<SessionDetails>>(`/Picklist/cvl/${formik.values.id}`)
        .then(async (response) => {
          if (response.isSuccess) {
            setDeleteModalOpen(false);
            toast.success(response.message);
            formik.resetForm();
            await fetchDataList();
          } else {
            throw new Error(response.message);
          }
        })
        .catch(() => { });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const formik: FormikProps<DataFieldWithTypes> = useFormik<DataFieldWithTypes>({
    initialValues: {
      id: 0,
      field: '',
      types: [],
      isActive: true,
    },
    validationSchema: Yup.object({
      field: Yup.string()
        .trim()
        .min(1, "CVL Name must be at least 1 characters")
        .max(100, "CVL Name must be at most 100 characters")
        .required("CVL Name is required")
        .test("field-duplicate-check", "CVL Name already exists",
          (value) => {
            return !dataFieldTypeList.some((item) => item.field.trim().toLowerCase() === value.trim().toLowerCase() &&
              item.id !== formik.values.id
            );
          }
        )
    }),
    onSubmit: async (values) => {
      if (editingRows?.length > 0) {
        setSubmitValues(values);
        apiRef.current.stopRowEditMode({ id: editingRows[0].id });
      } else {
        handleSubmit();
      }
    }
  });

  const handleSubmit = (updatedDataTypes?: DataTypeDetail[]) => {
    setDisableSubmit(true); // Set disableSubmit to true when the form is submitted
    setIsLoading(true);
    const values = submitValues || formik.values;
    const newTypesValues = (updatedDataTypes || typesValues).map(type => {
      // remove the temporary generated ids for new data types
      if (typeof type.id === "string") {
        type.id = 0;
      }
      return type;
    });
    if (newTypesValues.length <= 0) {
      setDisableSubmit(false);
      setIsLoading(false);
      return toast.error("Please add at least one CVL Option entry for this CVL Name");
    }
    if (values.id > 0) {
      api
        .put<ApiResponse<number>>("/Picklist/cvl", {
          id: values.id,
          field: values.field,
          types: newTypesValues,
          isActive: JSON.parse(`${formik.values.isActive}`)
        })
        .then(async (response) => {
          if (response.isSuccess) {            toast.success(response.message);
            await fetchDataList();
          } else {
            throw new Error(response.message);
          }
        });
    } else {
      api
        .post<ApiResponse<number>>("/Picklist/cvl", {
          id: values.id,
          field: values.field,
          types: newTypesValues,
          isActive: JSON.parse(`${formik.values.isActive}`)
        })
        .then(async (response) => {
          if (response.isSuccess) {
            toast.success(response.message);
            await fetchDataList();
          } else {
            throw new Error(response.message);
          }
        });
    }
    clearAllStates();
  };

  const clearAllStates = () => {
    setTypesValues([]);
    setDataField(null);
    setEditingRows([]);
    setSubmitValues(null);
    setRowModesModel({});
    setDisableSubmit(false);
    setIsLoading(false);
    setShowAddUpdateModal(false);
    setCellErrors({});
    formik.resetForm();
  };

  useEffect(() => {
    if (Object.keys(cellErrors).length <= 0 && editingRows?.length <= 0) {
      setRowModesModel((prevModel) => {
        const newModel = { ...prevModel };

        // Set all rows to "View" mode
        Object.keys(newModel).forEach((rowId) => {
          newModel[rowId] = { mode: GridRowModes.View };
        });

        return newModel;
      });
    }
  }, [cellErrors, editingRows]);

  return (
    <>
      <div>
        {manageAppSetting && <Button variant="contained" onClick={handleAddClickOpen}>
          ADD NEW CVL
        </Button>}
        {showAddUpdateModal && <DataFieldTypeModal
          isOpen={showAddUpdateModal}
          onClose={handleCloseAddEditModal}
          formik={formik}
          isButtonDisabled={disableSubmit}
          typesValues={typesValues}
          setTypesValues={setTypesValues}
          dataField={dataField}
          setDataField={setDataField}
          apiRef={apiRef}
          editingRows={editingRows}
          setEditingRows={setEditingRows}
          handleSubmit={handleSubmit}
          submitValues={submitValues}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          cellErrors={cellErrors}
          setCellErrors={setCellErrors}
          rowModesModel={rowModesModel}
          setRowModesModel={setRowModesModel}
        />
        }
        <DeleteValidationModal
          addModalOpen={deleteModalOpen}
          handleCloseModal={handleCloseDeleteModal}
          deleteSubmit={deleteSubmit}
          fieldIdentifier={"Data Field"}
        />
      </div>
      <SettingGrid
        data={dataFieldTypeList}
        column={DataFieldTypeGridCollumns}
        sortByField="id"
        sortByOrder="desc"
        rowHeightGetter={() => 'auto'}
        columnVisibility={{
          actions: manageAppSetting,
        }}

      />
    </>
  );
}
